import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../../i18n';

import { focus } from '@wix/wix-vod-shared/widget';
import {
  InfoOverlayAnimate,
  InfoOverlayWrapper,
  ClosableOverlay,
  AllVideoCards,
  AllCardsClosableOverlay,
} from '@wix/wix-vod-shared/components';
import VideoInfo from './video-info/video-info';

import ShareVideo from './share-video/share-video';

export const INFO_OVERLAY_CONTENT = {
  VIDEO_INFO: 'videoInfo',
  SHARE_VIDEO: 'shareVideo',
  ALL_CARDS: 'allCards',
};

class ShareInfoOverlay extends React.PureComponent {
  static propTypes = {
    overlayContent: PropTypes.string,
    onClose: PropTypes.func,
    siteUrl: PropTypes.string,
    videoItem: PropTypes.object,
    channelData: PropTypes.object,
    videoCards: PropTypes.array,
    isPlayerInFullScreen: PropTypes.bool,
    onMoreClicked: PropTypes.func,
    onCardClick: PropTypes.func,
    instanceInfo: PropTypes.object,
    videoCardsAccessibilityLabels: PropTypes.object,
    isRTL: PropTypes.bool,

    // Props derived from player-api-facade
    showPlayerUI: PropTypes.func,
  };

  componentDidUpdate(prevProps) {
    const { overlayContent } = this.props;

    if (!overlayContent && prevProps.overlayContent === overlayContent) {
      return;
    }

    const isVideoInfoVisible =
      overlayContent === INFO_OVERLAY_CONTENT.VIDEO_INFO;
    if (isVideoInfoVisible) {
      focus(this.videoInfoContainerRef);
    }

    const isShareVideoVisible =
      overlayContent === INFO_OVERLAY_CONTENT.SHARE_VIDEO;
    if (isShareVideoVisible) {
      focus(this.shareVideoContainerRef);
    }

    const isAllCardsVisible = overlayContent === INFO_OVERLAY_CONTENT.ALL_CARDS;
    if (isAllCardsVisible) {
      focus(this.allCardsContainerRef);
    }
  }

  shareVideoContainerRef = null;
  videoInfoContainerRef = null;
  allCardsContainerRef = null;

  saveRef = (name, ref) => {
    this[name] = ref;
  };

  handleClose = () => {
    this.props.showPlayerUI();
    this.props.onClose();
  };

  render() {
    const {
      overlayContent,
      siteUrl,
      instanceInfo,
      channelData,
      videoItem,
      videoCards,
      videoItem: {
        posterUrl,
        title,
        description,
        publisher,
        durationStr,
        genre,
        cast,
        crew,
      },
      isPlayerInFullScreen,
      onMoreClicked,
      videoCardsAccessibilityLabels,
      onCardClick,
      isRTL,
    } = this.props;

    const prevButtonAriaLabel = i18n.t(
      'accessibility.action-cards.all-cards.slide-to-prev-card',
    );
    const nextButtonAriaLabel = i18n.t(
      'accessibility.action-cards.all-cards.slide-to-next-card',
    );
    const getSlideAriaLabel = (variables) =>
      i18n.t('accessibility.action-cards.all-cards.slide-contents', variables);

    return (
      <InfoOverlayWrapper isActive={overlayContent} dir={isRTL ? 'rtl' : 'ltr'}>
        <section ref={(node) => this.saveRef('videoInfoContainerRef', node)}>
          <InfoOverlayAnimate
            when={overlayContent === INFO_OVERLAY_CONTENT.VIDEO_INFO}
          >
            <ClosableOverlay
              dataHook="closable-overlay"
              onClose={this.handleClose}
              isInFullScreen={isPlayerInFullScreen}
              closeButtonAriaLabel={i18n.t('accessibility.close-info-overlay')}
              posterUrl={posterUrl}
              isRTL={isRTL}
            >
              <VideoInfo
                title={title}
                description={description}
                posterUrl={posterUrl}
                publisher={publisher}
                genre={genre}
                durationStr={durationStr}
                cast={cast}
                crew={crew}
                durationAriaLabel={i18n.t('accessibility.duration', {
                  durationStr,
                })}
                isPlayerInFullScreen={isPlayerInFullScreen}
              />
            </ClosableOverlay>
          </InfoOverlayAnimate>
        </section>

        <section ref={(node) => this.saveRef('shareVideoContainerRef', node)}>
          <InfoOverlayAnimate
            when={overlayContent === INFO_OVERLAY_CONTENT.SHARE_VIDEO}
          >
            <ClosableOverlay
              dataHook="closable-overlay"
              onClose={this.handleClose}
              isInFullScreen={isPlayerInFullScreen}
              closeButtonAriaLabel={i18n.t('accessibility.close-share-button')}
              isFullWidth
              isRTL={isRTL}
            >
              <ShareVideo
                channelData={channelData}
                videoItem={videoItem}
                siteUrl={siteUrl}
                instanceInfo={instanceInfo}
                onMoreClicked={onMoreClicked}
                isRTL={isRTL}
              />
            </ClosableOverlay>
          </InfoOverlayAnimate>
        </section>

        <section ref={(node) => this.saveRef('allCardsContainerRef', node)}>
          <InfoOverlayAnimate
            when={overlayContent === INFO_OVERLAY_CONTENT.ALL_CARDS}
          >
            <AllCardsClosableOverlay
              dataHook="closable-overlay"
              onClose={this.handleClose}
              isInFullScreen={isPlayerInFullScreen}
              closeButtonAriaLabel={i18n.t('accessibility.close-share-button')}
              closeOnBodyClick
              isFullWidth
              isFullWidthForced
              isRTL={isRTL}
            >
              <AllVideoCards
                cardsData={videoCards}
                getSlideAriaLabel={getSlideAriaLabel}
                prevButtonAriaLabel={prevButtonAriaLabel}
                nextButtonAriaLabel={nextButtonAriaLabel}
                {...videoCardsAccessibilityLabels}
                onCardClick={onCardClick}
              />
            </AllCardsClosableOverlay>
          </InfoOverlayAnimate>
        </section>
      </InfoOverlayWrapper>
    );
  }
}

export default ShareInfoOverlay;
